/* eslint-disable no-nested-ternary */
import React, { useContext, useMemo, useRef, useState } from 'react';

import moment from 'moment';
import 'mapbox-gl/dist/mapbox-gl.css';

import { Button, ProgressBar, Typography } from 'design_system/src';
import './recovery.scss';
import { getUser } from 'localStorage/authStorage';
import { useNavigate } from 'react-router-dom';
import { InterestPointTypeValues } from 'models/repossession/enums/repossession.enums';
import {
	RecoveryStatus,
	RecoveryStatusValues,
} from 'models/repossession/enums/recovery.enums';
import { Map, Marker, Layer, MapRef, Source } from 'react-map-gl';
import mapboxgl from 'utils/mapboxgl';
import { LineLayer, LngLatBounds } from 'mapbox-gl';
import type { FeatureCollection } from 'geojson';
import {
	updateRecovery,
	updateRoute,
} from 'models/repossession/controllers/repossession.controller';
import { RouteStatus } from 'models/repossession/enums/route.enums';
import { ReactComponent as Info } from 'design_system/src/static/icons/info.svg';
import { ReactComponent as Maps } from 'design_system/src/static/icons/mesh-fill.svg';
import { ReactComponent as Close } from 'design_system/src/static/icons/close-sm.svg';
import { momentLocal } from 'models/shared/helpers/dates';

import { repossessionMobileContext } from './RepossessionsMobileContext';

moment.locale('es');

export const RoutesMobile = () => {
	const mapRef = useRef<MapRef>(null);

	const navigate = useNavigate();
	const {
		loading,
		route,
		recoveries,
		completedRecoveries,
		setRoute,
		updateRecoveries,
	} = useContext(repossessionMobileContext);

	const [openGlobalMap, setOpenGlobalMap] = useState(false);

	const [viewState, setViewState] = useState({
		longitude: -99.12766,
		latitude: 19.42847,
		zoom: 10,
	});

	const goToNextRecovery = async () => {
		const nextRecovery = recoveries.filter((recovery) =>
			[
				RecoveryStatus.pending,
				RecoveryStatus.inProgress,
				RecoveryStatus.inSite,
			].includes(recovery.status),
		)[0];
		const p = [];
		if (nextRecovery.status === RecoveryStatus.pending) {
			p.push(
				updateRecovery(nextRecovery._id, {
					status: RecoveryStatus.inProgress,
				}).then((recovery) => updateRecoveries(recovery)),
			);
		}
		if (route?.status === RouteStatus.pending) {
			p.push(
				updateRoute(route._id, { status: RouteStatus.inProgress }).then(
					(newRoute) => setRoute(newRoute),
				),
			);
		}
		if (p.length) await Promise.all(p);
		navigate(`/routes/${nextRecovery._id}`);
	};
	const goToFinalReport = async () => {
		navigate('/routes/final-report');
	};

	const markers = useMemo(() => {
		return recoveries.map((currentRecovery, i) => {
			const Icon =
				InterestPointTypeValues[currentRecovery.interestPoint.type].icon;

			const location = currentRecovery.interestPoint.coordinates;

			return (
				<Marker
					key={currentRecovery._id}
					longitude={location.long}
					latitude={location.lat}
				>
					<div
						className={`br_xxl main_marker cursor_pointer ${
							InterestPointTypeValues[currentRecovery.interestPoint.type].color
						}`}
					>
						<div className="marker_info p_y_xs p_x_lg flex_center flex_gap_sm">
							<div
								className={`dim_lg pos_relative br_circle bg_${
									InterestPointTypeValues[currentRecovery.interestPoint.type]
										.realColor
								}_nocontrast`}
							>
								<Typography
									weight="400"
									scale="xsmall"
									textColor="neutral_0"
									className="pos_absolute center"
								>
									{i + 1}
								</Typography>
							</div>
							<Typography weight="600" scale="xsmall">
								{currentRecovery.repossession.loan.vehicle.internalId}
							</Typography>
						</div>
						<div className="marker_details p_y_xxs p_x_md flex_center z_index_1 pos_relative">
							<Icon className="dim_lg icon" />
							<div className="p_x_md flex_center_col">
								<Typography weight="600" scale="xxsmall">
									{momentLocal(
										currentRecovery?.interestPoint?.eventDate,
									).format('DD/MM/YYYY')}
								</Typography>
								<Typography weight="400" scale="xxsmall">
									{momentLocal(
										currentRecovery?.interestPoint?.eventDate,
									).format('HH:mm')}
								</Typography>
							</div>
						</div>
						<span className="arrow" />
					</div>
				</Marker>
			);
		});
	}, [recoveries]);

	const routeLayer = useMemo(() => {
		const source: FeatureCollection = {
			type: 'FeatureCollection',
			features: [
				{
					type: 'Feature',
					properties: {},
					geometry: {
						type: 'LineString',
						coordinates: recoveries.map((currentRecovery) => [
							currentRecovery.interestPoint.coordinates.long,
							currentRecovery.interestPoint.coordinates.lat,
						]),
					},
				},
			],
		};
		const layerStyle: LineLayer = {
			id: 'route',
			type: 'line',
			paint: {
				'line-color': '#888',
				'line-width': 3,
			},
		};
		return source?.features?.length ? (
			<Source type="geojson" data={source}>
				<Layer {...layerStyle} />
			</Source>
		) : null;
	}, [recoveries]);

	const openMap = () => {
		const bounds = new LngLatBounds();
		recoveries.forEach((recovery) => {
			bounds.extend([
				recovery.interestPoint.coordinates.long,
				recovery.interestPoint.coordinates.lat,
			]);
		});
		mapRef.current?.fitBounds(bounds, { padding: 100, maxZoom: 15 });
		setOpenGlobalMap(true);
	};

	return loading ? (
		<Typography
			className="text_center w_100_per m_t_xxl"
			scale="large"
			weight="600"
		>
			Cargando rutas...
		</Typography>
	) : (
		<div className="p_b_xxxl">
			<Typography className="w_100_per" scale="heading4" weight="600">
				{!route || route.status === RouteStatus.pending
					? `¡Hola ${getUser()?.name ?? ''}!`
					: completedRecoveries === recoveries.length
					? '¡Felicitaciones!'
					: '¡Continua así!'}
			</Typography>
			{route ? (
				<>
					<Typography className=" w_100_per" scale="large" weight="400">
						{completedRecoveries === recoveries.length
							? 'Haz finalizado tu ruta del día'
							: 'Estas son tus rutas del día'}
					</Typography>
					<Typography className=" w_100_per" scale="large" weight="400">
						{moment.utc(route.routeDate).format('dddd, D [de] MMMM')}
					</Typography>
					<div className="bg_neutral_200 border_solid border_1 border_neutral_500 br_xs m_t_md">
						{recoveries.map((recovery, i) => (
							<div
								key={recovery._id}
								className=" display_flex flex_align_center flex_gap_md  border_b_solid border_1 border_neutral_500 p_md "
							>
								<div className="br_md bg_primary_100 border_1 border_solid border_primary_300 p_y_xxs p_x_xs">
									<Typography
										className="text_center w_10_vp"
										scale="heading3"
										weight="600"
										textColor="primary_300"
									>
										{(i + 1).toString().padStart(2, '0')}
									</Typography>
								</div>
								<div className="display_flex flex_col flex_justify_between flex_grow_1">
									<Typography className="" scale="medium" weight="600">
										{recovery.repossession.loan.vehicle.internalId}
									</Typography>
									<div className="display_flex flex_align_center flex_gap_xs ">
										{(() => {
											const type =
												InterestPointTypeValues[recovery.interestPoint.type];
											return (
												<>
													<type.icon
														className={`dim_md text_${type.realColor}`}
													/>
													<Typography className="" scale="medium" weight="400">
														{type.title}
													</Typography>
												</>
											);
										})()}
									</div>
								</div>
								<Typography
									textColor={RecoveryStatusValues[recovery.status].color as any}
									scale="medium"
									weight="400"
								>
									{RecoveryStatusValues[recovery.status].title}
								</Typography>
							</div>
						))}
						<div className="p_md display_flex flex_align_center flex_gap_xs">
							<ProgressBar
								containerClassName="bg_neutral_400 h_sm"
								fillClassName="bg_primary_300 h_sm"
								percentage={(completedRecoveries * 100) / recoveries.length}
							/>
							<Button
								scale="small"
								className="m_l_md"
								variant="icon"
								subvariant="link"
								icon={<Maps />}
								onClick={() => openMap()}
							/>
						</div>
					</div>
					{route.status === RouteStatus.pending && (
						<Typography scale="medium" weight="400" className="m_t_md m_b_xxl">
							¿listo?
						</Typography>
					)}
					{completedRecoveries === recoveries.length ? (
						<>
							<div className="display_flex flex_gap_md m_t_md">
								<Info className="dim_xl text_primary_300" />
								<Typography scale="medium" weight="400">
									El siguiente paso que debes realizar es subir las fotos de las
									motos recuperadas en CEDIS.
								</Typography>
							</div>
							<Button
								scale="small"
								onClick={() => goToFinalReport()}
								className="next_recovery_button"
							>
								Registro Final de Ruta
							</Button>
						</>
					) : (
						<Button
							scale="small"
							onClick={() => goToNextRecovery()}
							className="next_recovery_button"
						>
							{route.status === RouteStatus.pending
								? 'Empezar ruta'
								: 'Continuar ruta'}
						</Button>
					)}
					<div className={`global_map ${openGlobalMap ? 'open' : ''}`}>
						<div className="global_map_cont">
							<Map
								mapLib={mapboxgl}
								mapboxAccessToken={process.env.REACT_APP_MAPBOX_API_KEY || ''}
								onMove={(evt) => setViewState(evt.viewState)}
								mapStyle="mapbox://styles/mapbox/streets-v9"
								ref={mapRef}
								{...viewState}
							>
								{markers}
								{routeLayer}
							</Map>
							<Close
								className="close_button dim_xxxl"
								onClick={() => setOpenGlobalMap(false)}
							/>
						</div>
					</div>
				</>
			) : (
				<Typography
					className="text_center w_100_per m_t_md"
					scale="large"
					weight="400"
				>
					Parece que no tienes ninguna ruta asignada por el momento.
				</Typography>
			)}
		</div>
	);
};
